<template>
  <div class="w-full min-h-full">
    <loading :active.sync="isLoading" :is-full-page="fullPageLoader" :color="loaderColor" />

    <div class="w-full min-h-full h-auto pb-6">
      <v-wait for="purchases">
        <half-circle-spinner slot="waiting" style="display: block; margin: 0 auto;" :animation-duration="1000" :size="60" color="#3d4852" />
        <div>
          <div v-if="purchases.length" id="purchases-list-view" class="mt-4">
            <v-client-table
              ref="purchasesTable"
              :data="purchases"
              :columns="purchasesTableColumns"
              :options="purchasesTableOptions"
              class="bg-white text-center rounded border border-grey-300"
            >
              <template slot="current_status" slot-scope="props">
                <div v-if="props.row.is_refunded">
                  {{ $t('frontend_groovesell_billing_step2_refunded') }}
                </div>
                <div v-else-if="props.row.is_transaction_cancelled">
                  {{ $t('frontend_groovesell_billing_step2_cancelled') }}
                </div>
                <div v-else-if="props.row.is_recurring_transaction && props.row.payments_left != 'Subscription ended'">
                  {{ $t('frontend_groovesell_billing_step2_in_sub') }}
                </div>
                <div
                  v-else-if="
                    props.row.is_recurring_transaction && props.row.payments_left && props.row.payments_left == 'Subscription ended'
                  "
                >
                  {{ $t('frontend_groovesell_billing_step2_sub_ended') }}
                </div>
                <div v-else>
                  {{ $t('frontend_paid') }}
                </div>
              </template>
              <template slot="child_row" slot-scope="props">
                <div class="flex flex-row w-full">
                  <div class="flex flex-col w-3/4 px-4">
                    <gd-tabs
                      :tabs="purchaseDetailTabs"
                      :current-tab="purchaseDetailsTab"
                      :wrapper-class="'pt-4 px-4 gs-default-tabs'"
                      :tab-class="'pb-2 mr-4 gs-default-tabs-item'"
                      :tab-active-class="'border-pink border-b-2 text-pink gs-default-tabs-item-active'"
                      :line-class="'gs-default-tabs-active-line'"
                      @onClick="handlePurchaseDetailTabChange"
                    />
                    <div v-if="purchaseDetailsTab === 'transaction'">
                      <div class="flex flex-row flex-wrap w-full py-4 px-4">
                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_analytics_product_name') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{ props.row.product_name }}</label>
                        </div>

                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_analytics_price_point') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{ props.row.price_point_name }}</label>
                        </div>

                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_analytics_gateway') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{ props.row.gateway }}</label>
                        </div>

                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_analytics_transaction_id_gateway') }})</label>
                          <label class="text-sm text-grey-700 mb-2">{{ props.row.gateway_transaction_id }}</label>
                        </div>

                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_analytics_date_and_time') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{ props.row.formatted_date_time }}</label>
                        </div>

                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_analytics_refund_period') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{ props.row.product_refund_period }} {{ $t('frontend_analytics_days') }}</label>
                        </div>

                        <div v-if="props.row.recurring_billing" class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_analytics_recurring_bill') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{ props.row.recurring_billing }}</label>
                        </div>
                        <div v-else class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_analytics_recurring_bill') }}</label>
                          <label class="text-sm text-grey-700 mb-2">N/A</label>
                        </div>

                        <div
                          v-if="props.row.recurring_billing && props.row.payments_left && props.row.payments_left != 99999999999"
                          class="w-1/3 pt-2 pb-2 pr-2 flex flex-col"
                        >
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_analytics_payments_left') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{ props.row.payments_left }}</label>
                        </div>
                      </div>
                    </div>
                    <div v-else-if="purchaseDetailsTab === 'customer'">
                      <div class="flex flex-row flex-wrap w-full py-4 px-4">
                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_analytics_customer') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{ props.row.buyer_fullname }}</label>
                        </div>
                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">Email</label>
                          <label class="text-sm text-grey-700 mb-2">{{ props.row.buyer_email }}</label>
                        </div>

                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_phone') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{
                            props.row.buyer_phone && props.row.buyer_phone.trim() != '' ? props.row.buyer_phone : 'N/A'
                          }}</label>
                        </div>

                        <div class="w-full pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_company') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{
                            props.row.buyer_company && props.row.buyer_company.trim() != '' ? props.row.buyer_company : 'N/A'
                          }}</label>
                        </div>

                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_ip_address') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{ props.row.buyer_ip }}</label>
                        </div>
                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_address') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{
                            props.row.buyer_address && props.row.buyer_address.trim() != '' ? props.row.buyer_address : 'N/A'
                          }}</label>
                        </div>
                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_city') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{
                            props.row.buyer_city && props.row.buyer_city.trim() != '' ? props.row.buyer_city : 'N/A'
                          }}</label>
                        </div>

                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_state') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{
                            props.row.buyer_state && props.row.buyer_state.trim() != '' ? props.row.buyer_state : 'N/A'
                          }}</label>
                        </div>
                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_country') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{
                            props.row.buyer_country && props.row.buyer_country.trim() != '' ? props.row.buyer_country : 'N/A'
                          }}</label>
                        </div>
                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_zipcode') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{
                            props.row.buyer_postal_code && props.row.buyer_postal_code.trim() != '' ? props.row.buyer_postal_code : 'N/A'
                          }}</label>
                        </div>
                      </div>
                    </div>
                    <div v-else-if="purchaseDetailsTab === 'affiliate'">
                      <div class="flex flex-row flex-wrap w-full py-4 px-4">
                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_affiliate') }}</label>
                          <label class="text-sm text-grey-700 mb-2"
                            >{{ props.row.affiliate_name && props.row.affiliate_name.trim() != '' ? props.row.affiliate_name : 'N/A' }}
                          </label>
                        </div>
                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_affiliate') }} Email</label>
                          <label class="text-sm text-grey-700 mb-2">{{
                            props.row.affiliate_email && props.row.affiliate_email.trim() != '' ? props.row.affiliate_email : 'N/A'
                          }}</label>
                        </div>

                        <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                          <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_username') }}</label>
                          <label class="text-sm text-grey-700 mb-2">{{
                            props.row.affiliate_username && props.row.affiliate_username.trim() != '' ? props.row.affiliate_username : 'N/A'
                          }}</label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="flex flex-col w-1/4 border-l border-grey-300 p-4">
                    <div class="my-auto flex flex-col">
                      <gd-button
                        v-if="props.row.gateway_type == 'card' && props.row.is_transaction_cancelled == 0"
                        size="auto"
                        primary
                        class="mb-4 p-4"
                        @click="downloadTransactionInvoice(props.row.id)"
                      >
                        {{ $t('frontend_analytics_download_invoice') }}
                      </gd-button>

                      <gd-button
                        v-if="
                          props.row.recurring_billing &&
                            props.row.payments_left != 'Subscription ended' &&
                            props.row.gateway_type == 'card' &&
                            !props.row.is_refunded &&
                            props.row.is_active &&
                            props.row.is_transaction_cancelled == 0
                        "
                        size="auto"
                        primary
                        class="mb-4 p-4"
                        @click="updateProfile(props.row.id)"
                      >
                        {{ $t('frontend_groovesell_billing_edit_invoice') }}
                      </gd-button>
                      <gd-button
                        v-if="
                          props.row.can_update_credit_card &&
                          props.row.recurring_billing &&
                          props.row.payments_left != 'Subscription ended' &&
                          (
                            props.row.gateway.toLowerCase() == 'stripe' || 
                            props.row.gateway.toLowerCase() == 'authorize.net' || 
                            props.row.gateway.toLowerCase() == 'nmi' || 
                            props.row.gateway.toLowerCase() == 'groovepay_automated' || 
                            props.row.gateway.toLowerCase() == 'stax' || 
                            props.row.gateway.toLowerCase() == 'paypal_commerce' ||
                            props.row.gateway.toLowerCase() == 'spreedly'
                          ) &&
                          !props.row.is_refunded &&
                          props.row.is_active &&
                          props.row.is_transaction_cancelled == 0
                        "
                        size="auto"
                        primary
                        class="mb-4 p-4"
                        @click="updatePaymentInfo(props.row.id)"
                      >
                        {{ $t('frontend_groovesell_billing_update_card_details') }}
                      </gd-button>
                    </div>
                  </div>
                </div>
              </template>
            </v-client-table>
          </div>
          <div v-else class="mt-8">
            <p>
              {{ $t('frontend_groovesell_billing_not_purchased_using_email') }}
            </p>
          </div>
        </div>

        <gd-modal name="user-profile-modal" :title="$t('frontend_groovesell_billing_update_invoice')" width="80%" bg-type="regular">
          <div class="flex flex-wrap w-full bg-white rounded p-4">
            <div class="flex flex-col w-1/2 mb-6 px-2">
              <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_groovesell_billing_first_name') }}</label>
              <text-input
                v-model="purchaseDetail.buyer_fname"
                :validator="$v.purchaseDetail.buyer_fname"
                :attribute="$t('frontend_first_name')"
                value=""
                :placeholder="$t('frontend_groovesell_billing_first_name')"
              />
            </div>
            <div class="flex flex-col w-1/2 mb-6 px-2">
              <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_groovesell_billing_last_name') }}</label>
              <text-input
                v-model="purchaseDetail.buyer_lname"
                :validator="$v.purchaseDetail.buyer_lname"
                :attribute="$t('frontend_last_name')"
                value=""
                :placeholder="$t('frontend_groovesell_billing_last_name')"
              />
            </div>
            <div class="flex flex-col w-1/2 mb-6 px-2">
              <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_phone') }}</label>
              <text-input
                v-model="purchaseDetail.buyer_phone"
                :validator="$v.purchaseDetail.buyer_phone"
                :attribute="$t('frontend_phone')"
                value=""
                :placeholder="$t('frontend_phone')"
              />
            </div>
            <div class="flex flex-col w-1/2 mb-6 px-2">
              <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_company') }}</label>
              <text-input
                v-model="purchaseDetail.buyer_company"
                :validator="$v.purchaseDetail.buyer_company"
                :attribute="$t('frontend_company')"
                value=""
                :placeholder="$t('frontend_company')"
              />
            </div>
            <div class="flex flex-col w-1/2 mb-6 px-2">
              <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_address') }}</label>
              <text-input
                v-model="purchaseDetail.buyer_address"
                :validator="$v.purchaseDetail.buyer_address"
                :attribute="$t('frontend_address')"
                value=""
                :placeholder="$t('frontend_address')"
              />
            </div>
            <div class="flex flex-col w-1/2 mb-6 px-2">
              <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_city') }}</label>
              <text-input
                v-model="purchaseDetail.buyer_city"
                :validator="$v.purchaseDetail.buyer_city"
                :attribute="$t('frontend_city')"
                value=""
                :placeholder="$t('frontend_city')"
              />
            </div>
            <div class="flex flex-col w-1/2 mb-6 px-2">
              <label class="font-semibold text-sm text-grey-700 mb-2">
                {{ $t('frontend_country') }}
              </label>

              <country-select
                v-model="purchaseDetail.buyer_country"
                :validator="$v.purchaseDetail.buyer_country"
                top-country="US"
                :class-name="country_select_classes"
                :country="purchaseDetail.buyer_country"
              />
            </div>
            <div class="flex flex-col w-1/2 mb-6 px-2">
              <label class="font-semibold text-sm text-grey-700 mb-2">
                {{ $t('frontend_state') }}
              </label>
              <region-select
                v-model="purchaseDetail.buyer_state"
                :validator="$v.purchaseDetail.buyer_state"
                :class-name="region_select_classes"
                :country="purchaseDetail.buyer_country"
                :region="purchaseDetail.buyer_state"
              />
            </div>
            <div class="flex flex-col w-1/2 mb-6 px-2">
              <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_zipcode') }}</label>
              <text-input v-model="purchaseDetail.buyer_postal_code" attribute="Zip" value="" placeholder="Zip" />
            </div>
          </div>

          <div class="mb-6 pr-6 flex justify-end">
            <gd-button size="auto" primary class="mb-4 p-4" @click="updateUserProfile">
              {{ $t('frontend_groovesell_billing_update_invoice') }}
            </gd-button>
          </div>
        </gd-modal>

        <gd-modal name="update-payment-info-modal" :title="$t('frontend_groovesell_update_payment_information')">
          <div class="flex flex-col px-4">
            <div class="mt-4">
              <div class="flex flex-row mt-4">
                <div class="w-full">
                  <label
                    class="
                      custom-lable
                      font-semibold
                      text-sm text-grey-700
                      mb-2
                    "
                    >Name On Card</label
                  >
                  <text-input
                    class="custom-input-height"
                    v-model.trim="card.name"
                    :validator="$v.card.name"
                    attribute="Name On Card"
                    placeholder="Name On Card"
                    value="card.name"
                  />
                </div>
              </div>
              <div class="flex flex-row mt-4">
                <div class="w-full">
                  <label
                    class="
                      custom-lable
                      font-semibold
                      text-sm text-grey-700
                      mb-2
                    "
                    >Credit Card Number</label
                  >
                  <div
                    class="
                      relative
                      h-10
                      text-grey-600
                      border border-solid border-grey-200
                      rounded-lg
                      bg-white
                      flex-col
                    "
                    :class="{
                      'border-pink-dark error-form-group':
                        $v.card.number.$error,
                    }"
                  >
                    <div
                      id="spreedly-number"
                      class="custom-input-height"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="flex flex-row mt-4">
                <div class="w-1/2 mr-2">
                  <label
                    class="
                      custom-lable
                      font-semibold
                      text-sm text-grey-700
                      mb-2
                    "
                    >Expiration Date</label
                  >
                  <div class="flex flex-row">
                    <div
                      class="
                        w-1/2
                        relative
                        h-10
                        text-grey-600
                        border border-solid border-grey-200
                        rounded-lg
                        bg-white
                        mr-2
                      "
                    >
                      <select
                        v-model="card.expirationMonth"
                        class="
                          px-4
                          w-full
                          h-full
                          outline-none
                          rounded-lg
                        "
                        :value="card.expirationMonth"
                      >
                        <option value="01">01</option>
                        <option value="02">02</option>
                        <option value="03">03</option>
                        <option value="04">04</option>
                        <option value="05">05</option>
                        <option value="06">06</option>
                        <option value="07">07</option>
                        <option value="08">08</option>
                        <option value="09">09</option>
                        <option value="10">10</option>
                        <option value="11">11</option>
                        <option value="12">12</option>
                      </select>
                    </div>
                    <div
                      class="
                        w-1/2
                        relative
                        h-10
                        text-grey-600
                        border border-solid border-grey-200
                        rounded-lg
                        bg-white
                        mr-2
                      "
                    >
                      <select
                        v-model="card.expirationYear"
                        class="
                          px-4
                          w-full
                          h-full
                          outline-none
                          rounded-lg
                        "
                        :value="card.expirationYear"
                      >
                        <option
                          v-for="(year, yearIndex) in next10Years"
                          :value="year"
                        >
                          {{ year }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="w-1/2">
                  <label
                    class="
                      custom-lable
                      font-semibold
                      text-sm text-grey-700
                      mb-2
                    "
                    >CVV</label
                  >
                  <div
                    class="
                      relative
                      h-10
                      text-grey-600
                      border border-solid border-grey-200
                      rounded-lg
                      bg-white
                      flex-col
                    "
                    :class="{
                      'border-pink-dark error-form-group':
                        $v.card.number.$error,
                    }"
                  >
                    <div
                      id="spreedly-cvv"
                      class="custom-input-height"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-row w-full my-6 justify-end">
              <gd-button
                size="auto"
                primary
                class="mb-4 p-4"
                :class="{
                  'disabled-button cursor-not-allowed': is_paynow_button_disabled
                }"
                @click="updateCardDetails"
              >
                {{ $t('frontend_groovesell_billing_update_card_details') }}
              </gd-button>
            </div>
          </div>
        </gd-modal>
      </v-wait>
    </div>
  </div>
</template>

<script>
import Form from 'vform';
import Swal from 'sweetalert2';
import Loading from 'vue-loading-overlay';
import axios from 'axios';
import { required, numeric, minLength, maxLength } from 'vuelidate/lib/validators';
import Cleave from 'vue-cleave-component';

export default {
  name: 'Step2',
  components: {
    Loading,
    Cleave
  },
  props: {
    purchases: {
      required: true
    }
  },
  validations: {
    purchaseDetail: {
      buyer_fname: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(50)
      },
      buyer_lname: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(50)
      },
      buyer_company: {
        required,
        maxLength: maxLength(50)
      },
      buyer_phone: {
        required
      },
      buyer_address: {
        required,
        maxLength: maxLength(60)
      },
      buyer_city: {
        required,
        maxLength: maxLength(40)
      },
      buyer_postal_code: {
        required,
        maxLength: maxLength(20)
      },
      buyer_country: {
        required
      },
      buyer_state: {
        required
      }
    },
    card: {
      name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(80)
      },
      number: {
        required,
        numeric,
        minLength: minLength(13),
        maxLength: maxLength(19)
      },
      cvv: {
        required,
        numeric,
        minLength: minLength(3),
        maxLength: maxLength(4)
      }
    }
  },
  data() {
    return {
      tabs: [
        {
          title: '<span class="text-sm font-semibold">Info <i class="mr-2 ml-1 text-xs fas fa-chevron-right" aria-hidden="true"></i><span>',
          value: 'Your Access Level'
        },
        {
          title:
            '<span class="text-sm font-semibold">Transactions <i class="mr-2 ml-1 text-xs fas fa-chevron-right" aria-hidden="true"></i><span>',
          value: 'Transactions'
        }
      ],
      currentTab: 'step2',
      processing_payment: false,
      purchasesTableColumns: ['product_name', 'human_readable_price', 'formatted_date_time', 'current_status'],
      purchasesTableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc'
        },
        sortable: ['product_name'],
        headings: {
          product_name: 'Product',
          human_readable_price: 'Price',
          current_status: 'Status',
          formatted_date_time: 'Date And Time'
        },
        filterByColumn: false,
        filterable: false,
        childRowTogglerFirst: false,
        perPage: 10000,
        rowClassCallback() {
          return 'hover:bg-grey-50 funnelRow';
        }
      },
      purchaseDetailTabs: [
        { title: 'Transaction', value: 'transaction' },
        { title: 'Customer', value: 'customer' },
        { title: 'Affiliate', value: 'affiliate' }
      ],
      purchaseDetailsTab: 'transaction',
      fullPageLoader: true,
      isLoading: false,
      domLayout: 'autoHeight',
      loaderColor: '#FD6481',
      complete: true,
      creditCardGatewayType: null,
      creditCardGatewayId: 0,
      cardNumberOptions: {
        creditCard: true
      },
      card: {
        name: '',
        number: '',
        cvv: '',
        expirationYear: new Date().getFullYear(),
        expirationMonth: '01'
      },
      payment_info_form: new Form({
        paymentMethodId: null,
        setupIntentClientSecret: null,
        authNetDataDescriptor: null,
        authNetDataValue: null,
        gatewayType: null
      }),
      selectedTransactionId: 0,
      purchaseDetail: {
        id: null,
        buyer_fname: null,
        buyer_lname: null,
        buyer_phone: null,
        buyer_company: null,
        buyer_address: null,
        buyer_city: null,
        buyer_country: null,
        buyer_state: null,
        buyer_postal_code: null
      },
      genericUrl: `${window.config.apiUrl.split('/api')[0]}/api`,
      spreedlyEnvironmentKey: null
    };
  },

  computed: {
    country_select_classes() {
      return 'w-full px-4 h-10 text-grey-600 outline-none rounded-lg border border-solid border-grey-200 rounded-lg bg-white';
    },

    region_select_classes() {
      return 'w-full px-4 h-10 text-grey-600 outline-none rounded-lg border border-solid border-grey-200 rounded-lg bg-white';
    },
    next10Years() {
      const currentYear = new Date().getFullYear();
      const yearsArray = [];

      for (let i = currentYear; i <= currentYear + 10; i++) {
        yearsArray.push(i);
      }

      return yearsArray;
    },

    is_paynow_button_disabled() {
      if (this.processing_payment || !this.complete) {
        return true;
      } 
        return false;
      
    }
  },
  methods: {
    async createSpreedlyInstance(envKey)
    {
      console.log('Its spreedly');
      let self = this;
      Spreedly.init(envKey, {
        "numberEl": "spreedly-number",
        "cvvEl": "spreedly-cvv"
      });
      Spreedly.on("ready", function() {
        Spreedly.setStyle("number", "width: 100%; color: rgb(134, 137, 164); padding: 11px 14px;");
        Spreedly.setStyle("cvv", "width: 100%; color: rgb(134, 137, 164); padding: 11px 14px;");
        Spreedly.setStyle("placeholder", "color: #a0aec0; font-size: 14.5px;");
        Spreedly.setFieldType("number", "text");
        Spreedly.setNumberFormat('prettyFormat');
        Spreedly.setPlaceholder('number', 'Number');
        Spreedly.setPlaceholder('cvv', 'CVV');
      });
      Spreedly.on('errors', function(errors) {
        for (var i=0; i < errors.length; i++) {
          var error = errors[i];
          console.log(error);
        };
      });

      Spreedly.on('paymentMethod', (token, pmData) => {
        this.submitSpreedlyForm(token);
      });

      if ( self.spreedlyGatewayPublicKey ){
        Spreedly.stripeRadar(self.spreedlyGatewayPublicKey, function (radar_session_id) {
          if(self.checkoutStepType == 1 || self.checkoutStepType == 2){
            self.onestep_billing_form.stripeRadarSessionID = radar_session_id;
          } else {
            self.payment_info_form.stripeRadarSessionID = radar_session_id;
          }
        })
      }
    },
    async submitSpreedlyForm(token)
    {
      let self = this;
      self.payment_info_form.transactionId = self.selectedTransactionId
      self.payment_info_form.gatewayType = 'card'
      self.payment_info_form.spreedlyCardToken = token

      let captureUrl = `${this.genericUrl}/groovesell/update-payment-info`
      let payment_request_data = await self.payment_info_form.post(captureUrl)

      let payment_data = payment_request_data.data
      let payment_status = payment_data.status

      if (payment_status == 'captured') {
        self.processing_payment = false
        self.hideLoader()

        let { value: accept } = await Swal.fire(
          'Payment Info Updation Successful',
          'Your payment info for this transaction has been updated',
          'success'
        )

        if (accept) {
          self.$root.$emit('modal-close', 'update-payment-info-modal')
        }
      } else {
        self.processing_payment = false
        self.hideLoader()

        let { value: accept } = await Swal.fire(
          'Payment Info Updation Unsuccessful',
          'Your payment info for this transaction has not been updated. Please try again with a different card',
          'error'
        )

        if (accept) {
          self.$root.$emit('modal-close', 'update-payment-info-modal')
        }
      }
    },

    handlePurchaseDetailTabChange(newTab) {
      this.purchaseDetailsTab = newTab;
    },

    async reloadData() {
      this.$wait.start('purchases');
      try {
        const { data } = await axios.get(`${this.genericUrl}/groovesell/purchases`);

        if (data.status == 'success') {
          this.purchases = data.purchases;
        } else {
          Swal.fire(this.$t('frontend_error'), this.$t('frontend_analytics_billing_unable_load_pur'), 'error');
        }
      } catch (e) {
        Swal.fire(this.$t('frontend_error'), this.$t('frontend_analytics_billing_unable_load_pur'), 'error');
      }
      this.$wait.end('purchases');
    },

    async updatePaymentInfo (transactionId) {
      let paymentInfo = {}
      let self = this

      self.showLoader()

      await axios
        .get(`${this.genericUrl}/groovesell/fetch-transaction-payment-info`, {
          params: { transaction_id: transactionId }
        })
        .then(({ data }) => {
          self.selectedTransactionId = transactionId
          self.creditCardGatewayType = data.card_gateway_type

          console.log(data);

          setTimeout(() => {
            this.createSpreedlyInstance(data.card_gateway_public_details['environment_key']);
          }, 200)

          this.$root.$emit('modal-open', 'update-payment-info-modal')

          self.hideLoader()
        })
        .catch(error => {
          self.hideLoader()
          Swal.fire({
            type: 'error',
            title: 'Error',
            text: 'Unable to fetch payment info. Please try again.'
          })
        })
    },

    async downloadTransactionInvoice(transactionId) {
      const self = this;
      self.showLoader();
      const invoiceLinkData = await axios.post(`${this.genericUrl}/groovesell/generate-invoice-download-link`, {
        transaction_id: transactionId
      });
      
      let a = document.createElement('a');
      a.href = invoiceLinkData.data.download_link.replace(/^http:\/\//i, 'https://');
      a.click();

      self.hideLoader();
    },

    async updateCardDetails() {
      if (!this.is_paynow_button_disabled) {
        const self = this;

        const { value: accept } = await Swal.fire({
          title: this.$t('frontend_sure'),
          text: this.$t('frontend_groovesell_swal_update_your_payment_info'),
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: this.$t('frontend_groovesell_swal_btn_confirm_update_payment_info')
        });

        if (accept) {
          self.processing_payment = true
          await this.oneStepSubmitPaymentInfoSpreedly()
        }
      }
    },

    async oneStepSubmitPaymentInfoSpreedly() {
      let self = this;
      var  isValid = true
      if(self.checkoutStepType == 1)
      {
        self.$v.onestep_billing_form.$touch();
        isValid = !self.$v.onestep_billing_form.$invalid;
      }

      if(isValid)
      {
        self.showLoader();
        var requiredFields = {};

        if( this.card.name )
          requiredFields["full_name"] = this.card.name;
        
        if( this.card.expirationMonth )
          requiredFields["month"] = this.card.expirationMonth;
        
        if( this.card.expirationYear )
          requiredFields["year"] = this.card.expirationYear;

        Spreedly.tokenizeCreditCard(requiredFields);
      }
      else
      {
        Swal.fire({
          type: "error",
          title: "Error",
          text: "Please fill in all the required fields",
        });
      }
    },

    async updateProfile(transactionId) {
      const self = this;
      self.showLoader();
      const purchaseDeatai = await axios.post(`${this.genericUrl}/groovesell/purchase-detail`, {
        transaction_id: transactionId
      });
      self.purchaseDetail = purchaseDeatai.data.purchase[0];
      self.hideLoader();
      this.$root.$emit('modal-open', 'user-profile-modal');
    },
    async updateUserProfile() {
      this.$v.purchaseDetail.$touch();
      const isValid = !this.$v.purchaseDetail.$invalid;
      console.log(isValid);
      if (isValid) {
        const self = this;
        self.showLoader();
        const purchaseDetails = await axios.post(`${this.genericUrl}/groovesell/update-purchase-detail`, self.purchaseDetail);
        if (purchaseDetails.data.status == 'success') {
          const { data } = await axios.get('/purchases');
          if (data.status == 'success') {
            this.purchases = data.purchases;
          } else {
            Swal.fire(this.$t('frontend_error'), this.$t('frontend_analytics_billing_unable_load_pur'), 'error');
          }
          self.hideLoader();
          this.$root.$emit('modal-close', 'user-profile-modal');
        } else {
          Swal.fire({
            type: 'error',
            title: this.$t('frontend_error'),
            text: this.$t('frontend_groovesell_swal_billing_unable_update_details')
          });
          self.hideLoader();
        }
      }
    },

    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss">
.disabled-button {
  background-color: #fea7b8 !important;
}

#purchases-list-view {
  .VueTables--client table {
    overflow: auto !important;
  }

  .VueTables__search-field {
    margin-bottom: 10px;
  }

  .VueTables__heading {
    color: #8f92ab;
  }

  .VueTables__sort-icon {
    color: #8f92ab;
    margin-left: 8px;
  }

  td {
    font-weight: 400;
    outline: 2px solid transparent;
    outline-offset: 2px;
  }

  .VueTables__child-row-toggler--closed::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 7l-.707031.707031 6.125 6.125 1.875 1.875 1.875-1.875 6.125-6.125-.707031-.707031-6.125 6.125-1.167969 1.167969-1.167969-1.167969-6.125-6.125' /></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__child-row {
    text-align: center;
    border-bottom: 1px solid #ddd;
    border-left: 5px solid #fd6481;
  }

  .VueTables__child-row-toggler--open::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 1044.3622l-.707031-.707 6.125-6.125 1.875001-1.875 1.875 1.875 6.125 6.125-.707032.707-6.125-6.125-1.167968-1.168-1.167969 1.168-6.125 6.125' transform='translate(0-1030.3622)'/></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
  }

  .VueTables__row {
    border-bottom: 1px solid #ddd;
  }

  .VueTables__table th,
  td {
    text-align: left;
    padding: 16px;
  }

  tr.VueTables__child-row > td {
    padding: 0px !important;
  }

  thead {
    border-bottom: 1px solid #ddd;
  }

  .vue-pagination-ad {
    text-align: center;
  }

  .VueTables__limit {
    display: none;
  }

  .VuePagination__count {
    display: none;
  }

  .expandIcon {
    display: none;
  }

  .funnelRow:hover .expandIcon {
    display: block !important;
  }
}
</style>
